/**
 * `request_diagnose` — the owner's actual question, in one tool:
 *
 *     "why in this request i cannot send this, it returns this error"
 *
 * ==========================================================================
 * IT ANSWERS THAT WITHOUT A SINGLE BYTE OF ANY RESPONSE, AND HERE IS WHY IT
 * STILL WORKS
 * ==========================================================================
 *
 * The realistic causes of a request failing, in the order they actually happen:
 *
 *   1. **a variable is unset, empty or in the wrong environment** — by far the
 *      most common, and `wiring` + `selectedEnvironment` answer it exactly.
 *      "`Authorization` is wired to `{{apiToken}}`; `apiToken` is not defined
 *      in the selected environment `dev`, only in `prod`" is a complete answer
 *      to a 401 and needs no response body at all.
 *   2. **the wrong or missing `Content-Type`** — `problems` names it. A JSON
 *      body sent without the header is a 415 or a 400 every time.
 *   3. **auth wired to nothing, or inheriting from a collection with no auth**
 *      — `auth` + `collectionAuth` + `problems`.
 *   4. **a body shape mismatch** — `body.shape` gives the model the payload's
 *      key skeleton so it can compare it against the spec it is holding.
 *
 * What is NOT covered is *"the server said something surprising in the
 * payload"*, and the honest answer to the user for that case is in this tool's
 * own result: **open the response pane and tell me the status code and the
 * message.** Saying so is part of the feature — a model that silently omits
 * the one thing it cannot see gives worse advice than one that asks.
 *
 * ## Why there is no `lastResponse`, and this is a correction to PLAN §C2
 *
 * PLAN §C2 specified a `lastResponse` block (status, allowlisted headers, body
 * shape, an `errorHint`). **It cannot be built and should not be**, for two
 * independent reasons found while building it:
 *
 *   1. **There is no reachable last response.** The only headless copy lives on
 *      `store/tabsStore.js` (`requestTabs[i].response`, via
 *      `utils/workspaceTabPersistence.js:158`), and `src/mcp/` may not even
 *      NAME `tabsStore` — that is a pinned needle in BOTH
 *      `mcpUiRefresh.test.js:343` and `mcpBoardUiRefresh.test.js:374`, and it
 *      exists so no MCP module can drive the user's editor. A response is also
 *      only there while the request is OPEN in a tab, so the block would have
 *      been absent for most requests anyway.
 *   2. **`errorHint`'s bound does not hold.** It would have returned a string
 *      under an allowlisted key when it was ≤200 chars with no whitespace-free
 *      run over 40. `{"error":"invalid key sk_live_4eC39HqLyjWDarjtT1"}` passes
 *      that test — the run is 28 characters — and a Stripe secret key is
 *      exactly that shape. A length bound is not a secrecy bound, and a
 *      probabilistic gate is the one thing §B refuses to build.
 *
 * So responses stay out of this phase entirely and the refusal is structural:
 * there is no import, no field and no branch. If the owner wants response
 * bodies later it is **O29**, its own capability key with its own row and its
 * own default-off toggle. (`utils/responseBody.js`'s `getResponseBodyForAi`
 * gained its first caller on 13.09.2026 — the in-app assistant's
 * `get_last_response`, behind "Let AI read responses" — which is a separate
 * switch from any MCP capability; this handler still reads no response.)
 */
import {
  currentWorkspaceOrRefuse,
  findRequest,
  readCollections,
  workspaceReport,
} from '../collectionsDb';
import { authInheritanceOf } from '../authInheritance';
import { mcpError } from '../errors';
import { projectProtocol, requestKind } from '../protocolProjection';
import {
  contentTypeOf,
  projectAuth,
  projectBody,
  projectKvRows,
  projectUrl,
} from '../requestProjection';
import { templateRefNames } from '../templateRefs';
import { selectedEnvironmentReport, variableIndex } from '../variableScopes';

/** A problem the model can act on: a code, its subject, and a sentence. */
function problem(code, subject, message) {
  return { code, subject, message };
}

/**
 * Every `{{reference}}` in the record, with WHERE it is referenced. The list of
 * places is the diagnostic value: "`{{baseUrl}}` is used in the url" and
 * "`{{apiToken}}` is used in header Authorization" lead to different fixes.
 *
 * This walks the RECORD, not the projection, because a reference inside a
 * hidden header value is still a reference — hiding the value must not hide
 * the wiring, which is the entire argument of `templateRefs.js`.
 */
export function collectReferences(request) {
  const kind = requestKind(request);
  const places = new Map();
  const note = (name, where) => {
    const key = String(name);
    if (!places.has(key)) places.set(key, []);
    const list = places.get(key);
    if (!list.includes(where)) list.push(where);
  };
  const scan = (value, where) => {
    for (const name of templateRefNames(typeof value === 'string' ? value : '')) note(name, where);
  };

  const rowsOf = (list) =>
    (Array.isArray(list) ? list : []).filter((row) => row && String(row.key || '').trim());

  // A gRPC or WebSocket request ignores the HTTP fields, so only the fields it
  // really sends are scanned (card 5258c71d) - scanning the ignored ones would
  // report wiring that does nothing.
  if (kind === 'grpc') {
    scan(request?.grpcAddress, 'grpcAddress');
    for (const row of rowsOf(request?.grpcMetadata)) {
      scan(row.key, `metadata:${row.key}`);
      scan(row.value, `metadata:${row.key}`);
    }
    scan(request?.grpcRequestJson, 'message');
    return places;
  }
  if (kind === 'websocket') {
    scan(request?.url, 'url');
    for (const row of rowsOf(request?.wsHeaders)) {
      scan(row.key, `header:${row.key}`);
      scan(row.value, `header:${row.key}`);
    }
    return places;
  }

  scan(request?.url, 'url');
  for (const row of Array.isArray(request?.headers) ? request.headers : []) {
    if (!row || !String(row.key || '').trim()) continue;
    scan(row.key, `header:${row.key}`);
    scan(row.value, `header:${row.key}`);
  }
  for (const row of Array.isArray(request?.params) ? request.params : []) {
    if (!row || !String(row.key || '').trim()) continue;
    scan(row.value, `query:${row.key}`);
  }
  for (const row of Array.isArray(request?.cookies) ? request.cookies : []) {
    if (!row || !String(row.key || '').trim()) continue;
    scan(row.value, `cookie:${row.key}`);
  }
  for (const row of Array.isArray(request?.formDataFields) ? request.formDataFields : []) {
    if (!row || !String(row.key || '').trim()) continue;
    scan(row.value, `body:${row.key}`);
  }
  if (typeof request?.body === 'string') scan(request.body, 'body');
  else if (request?.body && typeof request.body === 'object') {
    scan(request.body.query, 'body');
    scan(request.body.variables, 'body');
  }
  const authRefs = projectAuth(request?.auth);
  for (const name of authRefs.wiredTo) note(name, `auth:${authRefs.type}`);

  return places;
}

/** What can be wrong with a gRPC request before it is sent. */
function grpcProblems(request, protocol) {
  const out = [];
  if (!protocol.address) {
    out.push(problem('url_empty', 'grpcAddress', 'This gRPC request has no server address.'));
  }
  if (!protocol.service || !protocol.method) {
    out.push(
      problem(
        'grpc_method_not_selected',
        'grpcMethod',
        'No gRPC service and method are selected. Load the .proto file or use server reflection in Apiboo, then pick the method.'
      )
    );
  }
  const messageText = typeof request?.grpcRequestJson === 'string' ? request.grpcRequestJson : '';
  if (messageText.trim() && protocol.message.parsed === false) {
    out.push(
      problem(
        'grpc_message_is_not_valid_json',
        'message',
        'The gRPC request message does not parse as JSON, so it cannot be encoded. Note that {{variables}} are substituted before sending, so an unquoted reference is a common cause.'
      )
    );
  }
  return out;
}

export async function requestDiagnose(args = {}) {
  const gate = currentWorkspaceOrRefuse();
  if (gate.error) return gate.error;
  const { workspace } = gate;

  const found = findRequest(readCollections(), args.request_id);
  if (!found) {
    return mcpError(
      'not_found',
      'No request with that id in the open workspace. Call collections_list with include_requests and use an id it returns.',
      { requestId: String(args.request_id ?? '') }
    );
  }
  const { collection, request } = found;

  const url = projectUrl(request.url, request.params);
  const headers = projectKvRows(request.headers, 'header');
  const body = projectBody(request);
  const auth = projectAuth(request.auth);
  const collectionAuth = projectAuth(collection.settings?.collection_auth);
  // request -> nearest folder -> ... -> collection, through the app's own
  // resolver (card 9334908f). Jumping straight to the collection reported a
  // folder override wrongly.
  const inheritance = authInheritanceOf(request, collection);
  const effective = {
    type: inheritance.effectiveAuthType,
    from: inheritance.effectiveAuthFrom,
    block: inheritance.effectiveAuth,
  };
  const kind = requestKind(request);
  const protocol = projectProtocol(request);
  const isHttpLike = kind === 'http' || kind === 'sse';

  const index = variableIndex({ collectionId: collection.id, collection, folderId: request.folder_id ?? null });
  const references = collectReferences(request);
  // An INHERITED auth block's {{variables}} are as much this request's wiring
  // as its own: a folder's `Bearer {{folderToken}}` is what Send substitutes.
  if (effective.from === 'folder' || effective.from === 'collection') {
    const where =
      effective.from === 'folder'
        ? `auth:${effective.type} (folder ${inheritance.effectiveAuthFolderId})`
        : `auth:${effective.type} (collection)`;
    for (const name of effective.block.wiredTo) {
      if (!references.has(name)) references.set(name, []);
      if (!references.get(name).includes(where)) references.get(name).push(where);
    }
  }
  const problems = [];

  /* ── 1. variables ─────────────────────────────────────────────────── */
  const wiring = [...references.entries()].map(([name, referencedIn]) => {
    const entry = index.get(name);
    let status = 'ok';
    if (!entry) status = 'undefined';
    else if (!entry.enabled) status = 'disabled';
    else if (entry.isEmpty) status = 'empty';

    if (status === 'undefined') {
      problems.push(
        problem(
          'variable_undefined',
          name,
          `{{${name}}} is used in ${referencedIn.join(', ')} but is not defined in the request's folders, the collection, the selected environment, the workspace or the globals. It will be sent literally as "{{${name}}}".`
        )
      );
    } else if (status === 'empty') {
      problems.push(
        problem(
          'variable_empty',
          name,
          `{{${name}}} is defined in ${entry.definedIn}${entry.definedInName ? ` (${entry.definedInName})` : ''} but has no value, so it substitutes to an empty string.`
        )
      );
    } else if (status === 'disabled') {
      problems.push(
        problem(
          'variable_disabled',
          name,
          `{{${name}}} is defined in ${entry.definedIn} but the row is switched off, so it does not substitute.`
        )
      );
    }

    return {
      name,
      referencedIn,
      definedIn: entry ? entry.definedIn : null,
      definedInName: entry ? entry.definedInName : null,
      isSecret: entry ? entry.isSecret : false,
      isEmpty: entry ? entry.isEmpty : true,
      status,
    };
  });

  // gRPC and WebSocket requests substitute {{variables}} like HTTP (address,
  // metadata, message; url, wsHeaders, outgoing messages — utils/protocolVariables),
  // so the per-variable checks above are the whole story for them too.

  /* ── 2. auth ──────────────────────────────────────────────────────── */
  const httpInherits = kind === 'http' && auth.type === 'inherit';
  if (httpInherits && effective.from === 'none') {
    problems.push(
      problem(
        'auth_inherit_but_collection_has_no_auth',
        collection.name || String(collection.id),
        inheritance.folderAuthChain.length
          ? 'This request inherits its authentication, none of its folders sets any, and the collection is set to No Auth — so nothing authenticates it. Set the auth on the request, on a folder, or on the collection.'
          : 'This request inherits its authentication from its collection, and the collection is set to No Auth — so nothing authenticates it. Set the auth on the request, or on the collection.'
      )
    );
  }
  if (httpInherits && effective.from === 'folder' && effective.type === 'noauth') {
    const stop = inheritance.folderAuthChain.find((f) => f.folderId === inheritance.effectiveAuthFolderId);
    problems.push(
      problem(
        'auth_inherit_stops_at_folder_with_no_auth',
        stop ? stop.name || stop.folderId : String(inheritance.effectiveAuthFolderId),
        'This request inherits its authentication, and an enclosing folder is set to No Auth — so inheritance stops there and the collection auth is NOT used. Set the folder to inherit, or set the auth on the request.'
      )
    );
  }
  if (effective.type !== 'noauth' && effective.type !== 'inherit') {
    // The SSE builder keeps its bearer token in `token`, the HTTP one in `bearer`.
    const credentialish = effective.block.fieldsSet.filter(
      (f) => f.startsWith(effective.type) || (kind === 'sse' && f === 'token')
    );
    if (!credentialish.length && !effective.block.wiredTo.length) {
      const where =
        { collection: 'inherited from the collection', folder: 'inherited from a folder' }[effective.from] ||
        'on the request';
      problems.push(
        problem(
          'auth_type_set_but_empty',
          effective.type,
          `Authentication is set to ${effective.type} (${where}) but its fields are empty, so no credential is sent.`
        )
      );
    }
  }
  if (auth.literalFields.length) {
    problems.push(
      problem(
        'auth_holds_a_literal_credential',
        auth.literalFields.join(', '),
        'A credential is typed directly into this request rather than referenced as a {{variable}}. This server cannot read it and cannot tell you whether it is the right one — suggest moving it into an environment variable.'
      )
    );
  }

  /* ── 3. content type and body ─────────────────────────────────────── */
  // gRPC and WebSocket requests ignore the HTTP body, method and headers.
  const contentType = isHttpLike ? contentTypeOf(request) : null;
  const sendsBody =
    isHttpLike && body.mode !== 'none' && (body.bytes > 0 || (body.fields || []).length > 0);
  if (sendsBody && body.mode === 'raw' && !contentType) {
    problems.push(
      problem(
        'no_content_type_on_body',
        'Content-Type',
        `This request sends a ${body.language || 'raw'} body and declares no Content-Type header. Most APIs answer that with a 400 or a 415.`
      )
    );
  }
  if (isHttpLike && contentType && body.mode === 'raw' && body.language === 'json' && !/json/i.test(contentType)) {
    problems.push(
      problem(
        'content_type_mismatch',
        contentType,
        `The body is written as JSON but Content-Type is "${contentType}".`
      )
    );
  }
  if (isHttpLike && body.mode === 'raw' && body.language === 'json' && body.parsed === false) {
    problems.push(
      problem(
        'body_is_not_valid_json',
        'body',
        'The body is set to JSON but does not parse as JSON. Note that {{variables}} are substituted before sending, so an unquoted reference is a common cause.'
      )
    );
  }
  if (sendsBody && ['GET', 'HEAD', 'DELETE'].includes(String(request.method || '').toUpperCase())) {
    problems.push(
      problem(
        'body_on_a_method_that_usually_has_none',
        String(request.method).toUpperCase(),
        `This is a ${String(request.method).toUpperCase()} with a body. Some servers and proxies drop it silently.`
      )
    );
  }

  /* ── 4. the url ───────────────────────────────────────────────────── */
  const rawUrl = typeof request.url === 'string' ? request.url.trim() : '';
  if (kind === 'grpc') {
    problems.push(...grpcProblems(request, protocol));
  } else if (kind === 'websocket' && rawUrl && !/^wss?:\/\//i.test(rawUrl) && !/^\{\{/.test(rawUrl)) {
    // `WebSocketBuilder.handleConnect` checks the SUBSTITUTED url, so a url that
    // starts with a {{variable}} (`{{wsBase}}/feed`) may supply the scheme.
    problems.push(
      problem(
        'websocket_url_scheme',
        'url',
        'A WebSocket url must start with ws:// or wss://, or with a {{variable}} that supplies one — Apiboo refuses to connect otherwise.'
      )
    );
  } else if (!rawUrl) {
    problems.push(problem('url_empty', 'url', 'This request has no url.'));
  } else if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(rawUrl) && !/^\{\{/.test(rawUrl)) {
    problems.push(
      problem(
        'url_has_no_scheme',
        'url',
        'The url does not start with a scheme (http:// or https://) or with a {{variable}} that could supply one.'
      )
    );
  }

  /* ── 5. duplicate headers ─────────────────────────────────────────── */
  const seen = new Map();
  let sentHeaders = headers;
  if (kind === 'websocket') sentHeaders = protocol.headers;
  else if (kind === 'grpc') sentHeaders = protocol.metadata;
  for (const h of sentHeaders) {
    if (!h.enabled) continue;
    const k = h.key.toLowerCase();
    seen.set(k, (seen.get(k) || 0) + 1);
  }
  for (const [name, count] of seen) {
    if (count > 1) {
      problems.push(
        problem('duplicate_header', name, `The header "${name}" is set ${count} times and enabled each time.`)
      );
    }
  }

  return {
    workspace: workspaceReport(workspace),
    request: {
      id: String(request.id),
      name: String(request.name || ''),
      method: String(request.method || 'GET').toUpperCase(),
      url: url.url,
      type: kind,
      collectionId: String(collection.id),
      collectionName: String(collection.name || ''),
    },
    selectedEnvironment: selectedEnvironmentReport(),
    wiring,
    headers,
    body,
    // gRPC / WebSocket fields, through the same projection as request_read.
    protocol,
    auth: {
      declared: auth.type,
      effective: effective.type,
      // request | folder | collection | none - the app's own resolver.
      effectiveFrom: effective.from,
      effectiveFolderId: inheritance.effectiveAuthFolderId,
      appliesToType: inheritance.authAppliesToType,
      wiredTo: effective.block.wiredTo,
      fieldsSet: effective.block.fieldsSet,
      literalFields: auth.literalFields,
    },
    folderAuthChain: inheritance.folderAuthChain,
    collectionAuth,
    problems,
    // The honest half of the answer. Stated as data rather than left to the
    // model to infer from an absent key.
    response: null,
    responseUnavailable: {
      reason: 'responses_are_not_readable_over_mcp',
      message:
        'This server never reads a response body, a response header or a status code — a login response IS a credential. If none of the problems above explains the failure, ask the user to open the response pane and tell you the status code and the message.',
    },
    scopesConsulted: [],
    scopesUnavailable: [],
  };
}
